import React, { useCallback, useEffect, useRef, useState } from 'react';
import useStyles from '../routes/user-submitted-lab-results-details/UserSubmittedLabResultDetails.styles';
import { TextField } from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

interface UserSubmittedLabResultHeaderProps {
  userSubmittedLabResult: {
    resultId: string;
    resultUploaded: string;
    status: string;
  };
  laboratoryName: string;
  setLaboratoryName: (laboratoryName: string) => void;
  sampleDate: string | undefined;
  setSampleDate: (date: string | undefined) => void;
  formatDate: (date: string | undefined) => string | undefined;
  setIsLabNameValid: (valid: boolean) => void;
  setIsDateValid: (valid: boolean) => void;
  isLabNameValid: boolean;
  isDateValid: boolean;
}

const UserSubmittedLabResultHeader: React.FC<UserSubmittedLabResultHeaderProps> = props => {
  const {
    userSubmittedLabResult,
    laboratoryName,
    setLaboratoryName,
    sampleDate,
    setSampleDate,
    formatDate,
    setIsLabNameValid,
    setIsDateValid,
    isLabNameValid,
    isDateValid,
  } = props;
  const classes = useStyles();
  const initialNameRenderRef = useRef(true);
  const initialDateRenderRef = useRef(true);

  const status = userSubmittedLabResult.status.replace(/([A-Z])/g, ' $1');
  const finalStatus = status.charAt(0).toUpperCase() + status.slice(1);

  const validateNameForm = useCallback(() => {
    if (laboratoryName === undefined || laboratoryName === '') {
      setIsLabNameValid(false);
    } else if (laboratoryName) {
      if (/[A-Za-z0-9s]+/.test(laboratoryName) && laboratoryName.length <= 255) {
        setIsLabNameValid(true);
      } else {
        setIsLabNameValid(false);
      }
    }
  }, [laboratoryName, setIsLabNameValid]);

  const validateDateForm = useCallback(
    (tempDate?: string) => {
      if (!tempDate) {
        if (
          sampleDate === undefined ||
          sampleDate === '' ||
          new Date() < new Date(sampleDate) ||
          new Date(sampleDate) < new Date('1900-01-01')
        ) {
          setIsDateValid(false);
        } else if (sampleDate) {
          const dateObj = new Date(sampleDate);
          if (!isNaN(dateObj.getTime()) && sampleDate.length > 0) {
            setIsDateValid(true);
          } else {
            setIsDateValid(false);
          }
        }
      } else {
        if (
          tempDate === undefined ||
          tempDate === '' ||
          new Date() < new Date(tempDate) ||
          new Date(tempDate) < new Date('1900-01-01')
        ) {
          setIsDateValid(false);
        } else if (tempDate) {
          const dateObj = new Date(tempDate);
          if (!isNaN(dateObj.getTime()) && tempDate.length > 0) {
            setIsDateValid(true);
          } else {
            setIsDateValid(false);
          }
        } else {
        }
      }
    },
    [sampleDate, setIsDateValid]
  );

  useEffect(() => {
    initialNameRenderRef.current = true;
    initialDateRenderRef.current = true;
    if (
      userSubmittedLabResult.status === 'inProgress' ||
      userSubmittedLabResult.status === 'inReview'
    ) {
      validateNameForm();
      validateDateForm();
    }
  }, []);

  useEffect(() => {
    if (!initialNameRenderRef.current) {
      validateNameForm();
    }
    if (!initialDateRenderRef.current) {
      validateDateForm();
    }
  }, [laboratoryName, sampleDate, validateNameForm, validateDateForm]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setLaboratoryName(value);
    initialNameRenderRef.current = false;
  };

  const handleDateChange = (date: Date | null | undefined) => {
    if (date !== null && date !== undefined && date?.toString() !== 'Invalid Date') {
      const tempDate = new Date(date);
      const correctedDate = new Date(tempDate.getTime() - tempDate.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 10);
      setSampleDate(correctedDate);
      validateDateForm(correctedDate);
      initialDateRenderRef.current = false;
    } else {
      setIsDateValid(false);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <table className={classes.tableHeader}>
        <tbody>
          <tr>
            <td className={classes.cell}>
              <div className={classes.headerCell}>
                <p>
                  <strong>ID (Status):</strong>
                </p>
                <p style={{ marginLeft: '10px' }}>
                  {userSubmittedLabResult?.resultId + ` (${finalStatus})`}
                </p>
              </div>
            </td>
            <td className={classes.cell}>
              <div className={classes.headerCell}>
                <strong>Laboratory Name:</strong>
                <TextField
                  name="labName"
                  type="text"
                  id="laboratory-name"
                  value={laboratoryName}
                  onChange={handleNameChange}
                  error={!isLabNameValid}
                  variant="outlined"
                  size="small"
                  required
                  style={{ marginLeft: '11px', width: '35%' }}
                  disabled={
                    userSubmittedLabResult.status === 'completed' ||
                    userSubmittedLabResult.status === 'rejected'
                  }
                />
              </div>
            </td>
          </tr>
          <tr>
            <td className={classes.cell}>
              <div className={classes.headerCell}>
                <p>
                  <strong>Result Uploaded:</strong>
                </p>
                <p style={{ marginLeft: '10px' }}>
                  {formatDate(userSubmittedLabResult?.resultUploaded)}
                </p>
              </div>
            </td>
            <td className={classes.cell}>
              <div className={classes.headerCell}>
                <strong>Sample Date:</strong>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    name="sampleDate"
                    InputProps={{
                      style: {
                        height: 42,
                      },
                    }}
                    margin="dense"
                    format="dd/MM/yyyy"
                    label="DD/MM/YYYY"
                    value={sampleDate ?? null}
                    onChange={handleDateChange}
                    error={!isDateValid}
                    style={{ marginLeft: '14px', width: '37%' }}
                    inputVariant="outlined"
                    onAccept={date => date && handleDateChange(date)}
                    disableFuture
                    maxDateMessage="Date of sample should not be in the future"
                    minDateMessage="Date should not be before 01-01-1900"
                    variant="inline"
                    required
                    disabled={
                      userSubmittedLabResult.status === 'completed' ||
                      userSubmittedLabResult.status === 'rejected'
                    }
                    onFocus={undefined}
                    onBlur={undefined}
                  />
                </MuiPickersUtilsProvider>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default UserSubmittedLabResultHeader;
